import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
	type ReactNode,
} from 'react'

type HelpDialogContextValue = {
	readonly isOpen: boolean
	readonly openHelpDialog: () => void
	readonly closeHelpDialog: () => void
	readonly setOpen: (open: boolean) => void
}

const HelpDialogContext = createContext<HelpDialogContextValue | null>(null)

type ProviderProps = {
	readonly children: ReactNode
}

export function HelpDialogProvider({ children }: ProviderProps) {
	const [isOpen, setIsOpen] = useState(false)

	const openHelpDialog = useCallback(() => {
		setIsOpen(true)
	}, [])

	const closeHelpDialog = useCallback(() => {
		setIsOpen(false)
	}, [])

	const value = useMemo<HelpDialogContextValue>(
		() => ({
			isOpen,
			openHelpDialog,
			closeHelpDialog,
			setOpen: setIsOpen,
		}),
		[isOpen, openHelpDialog, closeHelpDialog, setIsOpen],
	)

	return (
		<HelpDialogContext.Provider value={value}>
			{children}
		</HelpDialogContext.Provider>
	)
}

export function useHelpDialogContext(): HelpDialogContextValue | null {
	return useContext(HelpDialogContext)
}

export function useHelpDialog(): HelpDialogContextValue {
	const context = useHelpDialogContext()
	if (!context) {
		throw new Error('useHelpDialog must be used within a HelpDialogProvider')
	}
	return context
}
